/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { Star } from '@/components/Rating/Star';
import { useLabels } from '@/hooks/useLabels';
import { Checkbox, ProgressBar, Skeleton, Text, View } from '@az/starc-ui';
import cx from 'classnames';
import { Fragment } from 'react';
import type { RatingDistribution } from '../../interface';
import styles from './styles.module.scss';

const labelMap = {
  customerReviews: 'label_reviews_customerReviews',
  selectToFilter: 'label_reviews_selectToFilter',
  star: 'label_reviews_star',
  stars: 'label_reviews_stars',
};

export type RatingDistributionProps = {
  ratingDistribution: RatingDistribution[] | null;
  starFilter: number[];
  setStarFilter: React.Dispatch<React.SetStateAction<number[]>>;
  totalReviews: number | null;
};

const RATING_INDEXES = [0, 1, 2, 3, 4];

export function RatingDistributionFilter({
  ratingDistribution,
  starFilter,
  setStarFilter,
  totalReviews,
}: RatingDistributionProps) {
  const labels = useLabels(labelMap);
  const loading = !ratingDistribution?.length;

  return (
    <View>
      <View className={styles.ratingsContainer}>
        <View>
          <Text className={styles.reviewsTitle}>{labels.customerReviews}</Text>
          <Text className={styles.reviewsSubtitle}>{labels.selectToFilter}</Text>
        </View>

        <View className={styles.ratingList}>
          <Checkbox.Group
            name="selectedRating"
            value={starFilter.map(String)}
            onValueChange={(newValue) => {
              const starNumbers = newValue.map((star) => Number(star));
              // sort the values to have a consistent React Query key
              starNumbers.sort((a, b) => a - b);
              setStarFilter(starNumbers);
            }}
          >
            {RATING_INDEXES.map((index) => {
              const star = ratingDistribution?.[index]?.star ?? 5 - index;

              return (
                <Fragment key={index}>
                  <View>
                    <Checkbox
                      value={`${star}`}
                      className={styles.checkbox}
                      inputAttributes={{
                        'aria-label': `${star} ${star === 1 ? labels.star : labels.stars}`,
                      }}
                      label={
                        <Text className={styles.labelText} attributes={{ 'aria-hidden': true }}>
                          {star}
                        </Text>
                      }
                      disabled={loading || ratingDistribution[index]?.count === 0}
                    />
                  </View>

                  <View justify="center">
                    <Star />
                  </View>

                  <View>
                    <ProgressBar
                      label={`${star} ${star === 1 ? labels.star : labels.stars}`}
                      value={ratingDistribution?.[index].count ?? 0}
                      max={totalReviews ?? 0}
                      className={cx(styles.ratingPercentage, {
                        [styles.progressLoading]: loading,
                      })}
                      containerClassName={styles.progressOverride}
                    />
                  </View>

                  <View>
                    <Text
                      className={styles.labelText}
                      attributes={{ 'data-testid': `count-${star}`, 'aria-hidden': true }}
                    >
                      {loading ? (
                        <Skeleton borderRadius="small" />
                      ) : (
                        ratingDistribution[index]?.count
                      )}
                    </Text>
                  </View>
                </Fragment>
              );
            })}
          </Checkbox.Group>
        </View>
      </View>
    </View>
  );
}
